import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Icon from "@mdi/react";
import {
    mdiSchool,
    mdiAccountCheck,
    mdiTune,
    mdiHelpCircleOutline,
    mdiHelpCircle,
    mdiEmailMultipleOutline,
    mdiAccountGroup,
} from "@mdi/js";
import { useAuth } from "../../contexts/AuthContext";
import AppHeader from "../../components/AppHeader";
import ClassesHelpModal from "../../components/ClassesHelpModal";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ClassesPortal.css";
import "../../components/ClassesHelpModal.css";
/**
 * ClassesPortal - Portal landing page for the Class Management system
 * Portal type: Portal Landing (.portal-header-card)
 *
 * Wrapped by <ClassesSetupGate> at the route level in App.js — that gate
 * intercepts and renders the "Welcome to Class Management" setup card
 * when the org has no business address. This component assumes orgLocation
 * is set by the time it renders.
 */
const ClassesPortal = () => {
    const navigate = useNavigate();
    const { checkPermission } = useAuth();
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    const isTeamMember = !checkPermission("viewDashboard");

    return (
        <>
            <div className="dashboard-container org-console">
                <AppHeader />

                <div className="dashboard-main">
                    <div className="dashboard-content-wrapper" style={{ maxWidth: "900px" }}>
                        {/* Portal Header Card */}
                        <div className="sub-portal-header-card">
                            <div className="sub-portal-header-title-row">
                                {!isTeamMember && (
                                    <button onClick={() => navigate("/dashboard")} className="back-nav-btn">
                                        ← Dashboard
                                    </button>
                                )}
                                <h1 className="sub-portal-header-title">
                                    <Icon
                                        path={mdiSchool}
                                        size={1.2}
                                        color="var(--dl-color-purple-default)"
                                        style={{ marginRight: "8px", verticalAlign: "middle" }}
                                    />
                                    Classes
                                </h1>
                            </div>
                            <div className="classes-subtitle-row">
                                <p className="sub-portal-header-subtitle">
                                    Manage your studio&rsquo;s classes, terms, student enrolments, and check-in.
                                </p>
                                <button
                                    className="classes-help-trigger"
                                    onClick={() => setHelpOpen(true)}
                                    onMouseEnter={() => setHelpIconHovered(true)}
                                    onMouseLeave={() => setHelpIconHovered(false)}
                                    aria-label="Class Management Help"
                                    title="How It Works">
                                    <Icon
                                        path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                        size="1.62rem"
                                        color="var(--dl-color-purple-default)"
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Row 1 — Class Check-In (hero), Email Students, Manage Students */}
                        <div className="module-grid-row module-grid-row--no-stretch module-grid-row--hero-left">
                            {/* Check-In hero card — wider and taller */}
                            <Link to="/classes/checkin" className="module-card-hero classes-portal-checkin-card">
                                <div className="module-card-header">
                                    <h3 style={{ color: "var(--dl-color-purple-default)", margin: 0 }}>Class Check-In</h3>
                                    <Icon path={mdiAccountCheck} size={1.4} color="var(--dl-color-purple-default)" />
                                </div>
                                <p className="module-card-subtitle">
                                    Student check-in for today&rsquo;s classes <br></br>(optimised for touch screen
                                    tablets)
                                </p>
                            </Link>

                            {/* Email Students card */}
                            <Link to="/communications" className="module-card">
                                <div className="module-card-header">
                                    <h3
                                        style={{
                                            color: "var(--dl-color-purple-default)",
                                            fontSize: "1.25rem",
                                            margin: 0,
                                        }}>
                                        Email Students
                                    </h3>
                                    <Icon
                                        path={mdiEmailMultipleOutline}
                                        size={1.1}
                                        color="var(--dl-color-purple-default)"
                                    />
                                </div>
                                <p className="module-card-subtitle">Compose new emails or review what's already gone out</p>
                            </Link>

                            {/* Manage Students card */}
                            <Link to="/classes/students" className="module-card">
                                <div className="module-card-header">
                                    <h3
                                        style={{
                                            color: "var(--dl-color-purple-default)",
                                            fontSize: "1.25rem",
                                            margin: 0,
                                        }}>
                                        Manage Students
                                    </h3>
                                    <Icon
                                        path={mdiAccountGroup}
                                        size={1.1}
                                        color="var(--dl-color-purple-default)"
                                    />
                                </div>
                                <p className="module-card-subtitle">
                                    Manage enrolments, enquiries, upgrades and re-enrolments
                                </p>
                            </Link>
                        </div>

                        {/* Row 2 — Manage Classes Setup, full width */}
                        <Link to="/classes/manage" className="module-card-negative classes-portal-manage-row">
                            <div className="module-card-negative-content">
                                <p className="module-card-negative-title">Manage Classes / Setup</p>
                                <p className="module-card-negative-subtitle">
                                    Terms, Pricing, Instructors, Student Upload, Settings
                                </p>
                            </div>
                            <Icon path={mdiTune} size={1.2} color="var(--dl-color-gray-white)" />
                        </Link>
                    </div>
                </div>
            </div>

            <ClassesHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
        </>
    );
};

export default ClassesPortal;
